import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../components/ui/button-old'
import Loader from '../components/ui/loader.jsx'
import Validator from '../utils/validator'
import apiService from '../services/api.service'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'
import { PasswordInput } from '../components/ui/password-input'
import PasswordCriteriaValidator from '../components/PasswordCriteriaValidator'

const ResetPassword = () => {
  const { token, tenanturlname } = useParams()
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { t, languageId } = useLanguage()

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState(true)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)

  const currentLanguage = languageId === 2 ? 'ja' : 'en'

  // Validate token exists
  useEffect(() => {
    if (!token) {
      setTokenValid(false)
    }
  }, [token])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
    // Clear HTML5 validation message
    e.target.setCustomValidity('')
  }

  const handlePasswordFocus = () => {
    setIsPasswordFocused(true)
  }

  const handlePasswordBlur = () => {
    setIsPasswordFocused(false)
  }

  // Intercept HTML5 validation and show translated errors
  const handleInvalid = (e) => {
    e.preventDefault()
    const { name, validity } = e.target
    const newErrors = { ...errors }

    if (name === 'newPassword') {
      if (validity.valueMissing) {
        newErrors.newPassword = t('resetPassword.errors.passwordRequired')
      } else {
        const passwordValidation = Validator.isValidPassword(e.target.value)
        if (!passwordValidation.valid) {
          newErrors.newPassword = t(
            `resetPassword.errors.${passwordValidation.errorCode}`,
          )
        }
      }
    } else if (name === 'confirmPassword') {
      if (validity.valueMissing) {
        newErrors.confirmPassword = t('resetPassword.errors.confirmRequired')
      } else if (formData.newPassword !== e.target.value) {
        newErrors.confirmPassword = t('resetPassword.errors.passwordMismatch')
      }
    }

    setErrors(newErrors)
    // Set custom validity to prevent browser's default message
    e.target.setCustomValidity(' ')
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate new password
    if (!formData.newPassword) {
      newErrors.newPassword = t('resetPassword.errors.passwordRequired')
    } else {
      const passwordValidation = Validator.isValidPassword(formData.newPassword)
      if (!passwordValidation.valid) {
        newErrors.newPassword = t(
          `resetPassword.errors.${passwordValidation.errorCode}`,
        )
      }
    }

    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.errors.confirmRequired')
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.errors.passwordMismatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await apiService.post(
        '/api/kc/complete-password-reset',
        {
          resetToken: token,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
          clienttenantid: tenant?.tenantid || 1011,
          defaultlanguageid: languageId || 490,
        },
      )

      // Success
      setIsSuccess(true)

      // Redirect to signin after 3 seconds
      setTimeout(() => {
        const tenantUrlName = tenant?.tenanturlname || tenanturlname
        navigate(`/${tenantUrlName}/signin`)
      }, 3000)
    } catch (error) {
      console.error('Password reset error:', error)

      const errorCode = error?.data?.code
      const apiMessage = error?.data?.message
      const apiError = error?.data?.error
      const normalizedMessage = apiMessage?.toLowerCase()
      const isTokenInvalid =
        normalizedMessage?.includes('expired') ||
        normalizedMessage?.includes('not found')
      const isPasswordHistoryError = normalizedMessage?.includes(
        'must not be equal to any of last 4 passwords',
      )
      const isRateLimit =
        error.isRateLimit ||
        normalizedMessage?.includes('rate limit') ||
        error.message === 'Rate limit exceeded. Please try again later.'
      const isAccountLocked =
        apiMessage?.includes('temporarily locked') ||
        apiMessage?.includes('multiple failed attempts')

      // Check for account lockout error
      if (isAccountLocked) {
        // Use translation for account locked message
        setErrors({ submit: t('resetPassword.errors.accountLocked') })
      } else if (isTokenInvalid) {
        setTokenValid(false)
      } else if (isPasswordHistoryError) {
        setErrors({
          submit: t('resetPassword.errors.invalidPasswordHistoryMessage'),
        })
      } else if (isRateLimit) {
        setErrors({ submit: t('resetPassword.errors.rateLimitExceeded') })
      } else if (apiMessage || apiError) {
        setErrors({ submit: t('resetPassword.errors.resetFailed') })
      } else {
        setErrors({ submit: t('resetPassword.errors.resetFailed') })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequestNewLink = () => {
    const tenantUrlName = tenant?.tenanturlname || tenanturlname
    navigate(`/${tenantUrlName}/forgot-password`)
  }

  return (
    <>
      {isLoading && (
        <Loader
          fullscreen
          label={t('resetPassword.resettingButton')}
          size="xl"
        />
      )}

      <div className="w-full px-6 mt-10 md:max-w-[600px] md:mx-auto">
        <div className="w-full px-6 md:max-w-[600px] md:mx-auto">
          {/* Reset Password - START */}
          {!tokenValid ? (
            /* Invalid/Expired Token State */
            <div className="text-center">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-theme-secondary mb-2">
                {t('resetPassword.invalidTokenTitle')}
              </h2>
              <p className="text-sm text-theme-secondary mb-6">
                {t('resetPassword.invalidTokenMessage')}
              </p>
              <Button variant="primary" onClick={handleRequestNewLink}>
                {t('resetPassword.requestNewLink')}
              </Button>
            </div>
          ) : isSuccess ? (
            /* Success State */
            <div className="text-center">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-theme-secondary mb-2">
                {t('resetPassword.successTitle')}
              </h2>
              <p className="text-sm text-theme-secondary mb-6">
                {t('resetPassword.successMessage')}
              </p>
              <div className="flex justify-center">
                <Loader size="md" labelColor="text-theme-primary" />
              </div>
            </div>
          ) : (
            /* Reset Password Form */
            <form
              className="form-container"
              onSubmit={handleSubmit}
              aria-label="Reset Password form"
            >
              <div className="text-center mb-4">
                <h1 className="text-base font-extrabold text-theme-secondary">
                  {t('auth.resetPassword.title')}
                </h1>
              </div>

              {/* Display general error */}
              {errors.submit && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* New Password Field */}
                <div className="grid gap-1">
                  <label
                    htmlFor="newPassword"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    {t('resetPassword.newPasswordLabel')}
                  </label>
                  <PasswordInput
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    onInvalid={handleInvalid}
                    onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                    autoComplete="new-password"
                    aria-required="true"
                    aria-invalid={errors.newPassword ? 'true' : 'false'}
                    aria-describedby={
                      errors.newPassword ? 'newPassword-error' : undefined
                    }
                  />

                  <PasswordCriteriaValidator
                    password={formData.newPassword}
                    language={currentLanguage}
                    isVisible={isPasswordFocused}
                  />

                  {errors.newPassword && (
                    <div
                      id="newPassword-error"
                      className="flex items-center gap-2 text-sm text-red-500"
                    >
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                      {errors.newPassword}
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div className="grid gap-1">
                  <label
                    htmlFor="confirmPassword"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    {t('resetPassword.confirmPasswordLabel')}
                  </label>
                  <PasswordInput
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onInvalid={handleInvalid}
                    // onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.confirmPassword
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                    autoComplete="new-password"
                    aria-required="true"
                    aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                    aria-describedby={
                      errors.confirmPassword
                        ? 'confirmPassword-error'
                        : undefined
                    }
                  />
                  {errors.confirmPassword && (
                    <div
                      id="confirmPassword-error"
                      className="flex items-center gap-2 text-sm text-red-500"
                    >
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>

                <Button
                  id="reset-btn"
                  variant="primary"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('resetPassword.resettingButton')
                    : t('auth.resetPassword.submitButton')}
                </Button>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={handleRequestNewLink}
                    className="text-sm text-theme-primary hover:underline"
                  >
                    {t('auth.forgotPassword.sendAnother')}
                  </button>
                </div>
              </div>
            </form>
          )}
          {/* Reset Password - END */}
        </div>
      </div>
    </>
  )
}

export default ResetPassword
